import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp,
  Clock,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkApproveModal({ 
  isOpen, 
  onClose, 
  scope = 'all', 
  bucket = null,
  onApproveComplete 
}) {
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showExceptions, setShowExceptions] = useState(false);
  const [exceptionsPage, setExceptionsPage] = useState(1);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPreview();
    }
  }, [isOpen, scope, bucket]);

  const fetchPreview = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { scope, page, page_size: 50 };
      if (bucket) params.bucket = bucket;
      
      const response = await axios.get(`${API}/coach/adjustments/preview`, { params });
      setPreview(response.data);
      setExceptionsPage(page);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError('Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSafe = async () => {
    setApproving(true);
    try {
      const body = { scope };
      if (bucket) body.bucket = bucket;
      
      const response = await axios.post(`${API}/coach/adjustments/approve-safe`, body);
      
      if (onApproveComplete) {
        onApproveComplete(response.data);
      }
      onClose();
    } catch (err) {
      console.error('Failed to approve:', err);
      setError('Failed to approve adjustments');
    } finally {
      setApproving(false);
    }
  };

  const handleViewExceptions = () => {
    setShowExceptions(true);
  };

  const handleBackToSummary = () => {
    setShowExceptions(false);
  };

  const handlePageChange = (newPage) => {
    fetchPreview(newPage);
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'cut': return 'bg-red-100 text-red-700';
      case 'increase': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {showExceptions ? 'Needs review' : 'Preview bulk approval'}
          </DialogTitle>
          {!showExceptions && !loading && preview && (
            <DialogDescription>
              Review adjustments before approving
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          ) : showExceptions ? (
            /* Exceptions List View */
            <div className="space-y-3">
              {preview.exceptions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No exceptions to review</p>
              ) : (
                <>
                  {preview.exceptions.map((exception, idx) => (
                    <div 
                      key={exception.adjustment_id || idx}
                      className="border rounded-lg p-4 bg-white"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="font-medium text-gray-900">{exception.name}</span>
                          <span className="text-gray-500 text-sm ml-2">{exception.email}</span>
                        </div>
                        <Badge className={getActionBadgeColor(exception.action)}>
                          {exception.action}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        {Math.round(exception.old_calories)} → {Math.round(exception.new_calories)} kcal
                      </div>
                      
                      <div className="space-y-1">
                        <span className="text-xs font-medium text-gray-500">Reason:</span>
                        {exception.reasons.map((reason, rIdx) => (
                          <div key={rIdx} className="flex items-center gap-2 text-sm text-orange-600">
                            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                            <span>{reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Pagination */}
                  {preview.exceptions_total > preview.exceptions_page_size && (
                    <div className="flex justify-center items-center gap-4 pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(exceptionsPage - 1)}
                        disabled={exceptionsPage <= 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-600">
                        Page {exceptionsPage} of {Math.ceil(preview.exceptions_total / preview.exceptions_page_size)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(exceptionsPage + 1)}
                        disabled={exceptionsPage >= Math.ceil(preview.exceptions_total / preview.exceptions_page_size)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            /* Summary View */
            <div className="space-y-6">
              {/* Counts */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 mb-1">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Safe to approve</span>
                  </div>
                  <div className="text-3xl font-bold text-green-700">
                    {preview.safe_count}
                  </div>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-orange-700 mb-1">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Needs review</span>
                  </div>
                  <div className="text-3xl font-bold text-orange-700">
                    {preview.exception_count}
                  </div>
                </div>
              </div>

              {/* Stale Warning */}
              {preview.stale_count > 0 && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Clock className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">
                    Note: Some pending adjustments are older than 7 days.
                  </span>
                </div>
              )}

              {/* Breakdown */}
              {preview.breakdown.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Safe adjustments breakdown:</h4>
                  <div className="space-y-2">
                    {preview.breakdown.map((item, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <Badge className={getActionBadgeColor(item.action)}>
                            {item.action}
                          </Badge>
                          <span className="text-sm text-gray-700 capitalize">
                            {item.bucket.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <span className="font-medium text-gray-900">
                          {item.count} client{item.count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No safe adjustments message */}
              {preview.safe_count === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No safe adjustments to approve.</p>
                  <p className="text-sm mt-1">All pending adjustments need manual review.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          {showExceptions ? (
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={handleBackToSummary}>
                Back to summary
              </Button>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <div className="flex gap-2">
                {preview && preview.exception_count > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={handleViewExceptions}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    View needs review
                  </Button>
                )}
                <Button
                  onClick={handleApproveSafe}
                  disabled={approving || !preview || preview.safe_count === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {approving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve safe adjustments
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
